import { FC, useMemo } from 'react';
import { useRouter } from 'next/router';
import { AnalyticsProvider as SDKAnalyticsProvider, type Environment as AnalyticsEnvironment } from '@1debit/analytics';

import { analyticsHandler } from '../eventRouter';

type Props = {
	children: React.ReactNode;
};

// Analytics sinks validate page identifiers as strict snake_case:
// ^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$
// We normalize the pathname into that format by:
// - stripping query/hash and leading slash
// - inserting underscores for camelCase boundaries
// - replacing any non-alphanumeric (including "/") with a single underscore
// - lowercasing and collapsing duplicate underscores
// - trimming leading/trailing underscores
// - ensuring it starts with a letter (prefix with "p_" if needed)
// Example: "/member/123/transactions" -> "member_123_transactions"
const toSnakeCasePageId = (pathname: string): string => {
	const cleanPath = pathname.split('?')[0].split('#')[0];
	const withoutLeadingSlash = cleanPath.startsWith('/') ? cleanPath.slice(1) : cleanPath;
	const withCamelBoundaries = withoutLeadingSlash.replace(/([a-z0-9])([A-Z])/g, '$1_$2');
	const replaced = withCamelBoundaries.replace(/[^a-zA-Z0-9]+/g, '_');
	let snake = replaced
		.toLowerCase()
		.replace(/_+/g, '_')
		.replace(/^_+|_+$/g, '');
	if (snake.length === 0) snake = 'root';
	if (!/^[a-z]/.test(snake)) snake = `p_${snake}`;
	return snake;
};

export const EventRouterAnalyticsProvider: FC<Props> = ({ children }) => {
	const router = useRouter();
	const { pathname } = router;
	const env = (process.env.NEXT_PUBLIC_DEPLOY_ENVIRONMENT || 'development') as AnalyticsEnvironment;
	const pageName = useMemo(() => toSnakeCasePageId(pathname), [pathname]);

	return (
		<SDKAnalyticsProvider handler={analyticsHandler} pageName={pageName} env={env}>
			{children}
		</SDKAnalyticsProvider>
	);
};

export default EventRouterAnalyticsProvider;
